import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import cytoscape, { CytoscapeOptions } from 'cytoscape';
import styles from './cytoscape/styles';
import { NgFlowDesignerOutputDirective } from './ng-flow-designer-output.directive';
import { NgFlowDesignerNodeDirective } from './ng-flow-designer-node.directive';
import {
  CancelTargetSelectionEvent,
  FlowMouseEvent,
  StartTargetSelectionEvent,
  TargetSelectionEvent,
} from './interfaces';
import { FlowDesignerService } from './flow-designer.service';
import { NgFlowDesignerService } from './ng-flow-designer.service';

@Component({
  selector: 'ng-flow-designer',
  template: ` <div #container class="flow-designer">
    <ng-content></ng-content>
  </div>`,
  styleUrls: ['./ng-flow-designer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NgFlowDesignerComponent implements OnInit, OnChanges, AfterContentInit, OnDestroy {
  constructor(
    private flowDesignerService: FlowDesignerService,
    private ngFlowDesignerService: NgFlowDesignerService,
    private ngZone: NgZone,
    private element: ElementRef,
  ) {}
  @ContentChildren(NgFlowDesignerNodeDirective) nodes!: QueryList<NgFlowDesignerNodeDirective>;
  @ViewChild('container', { static: true }) container: ElementRef;
  @Input() debug = false;
  @Input() wheelSensitivity = 0.05;
  @Input() minZoom = 0.7;
  @Input() maxZoom = 2;
  @Input() smallGridCellSize = 10;
  @Input() gridCellSize = 100;
  @Input() snapToGrid = true;
  @Input() moveOnArrows = true;
  @Input() arrowsMoveSensitive = 10;
  @Input() zoom = 1;
  @Input() panAnimationDuration = 500;
  @Input() panEnabled = true;
  @Input() zoomEnabled = true;
  @Input() pan: { x: number; y: number } = { x: 0, y: 0 };
  @Output() mouseDown: EventEmitter<any> = new EventEmitter();
  @Output() mouseUp: EventEmitter<FlowMouseEvent> = new EventEmitter();
  @Output() zoomChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() panChange: EventEmitter<{ x: number; y: number }> = new EventEmitter<{ x: number; y: number }>();
  @Output() targetSelection: EventEmitter<TargetSelectionEvent> = new EventEmitter<TargetSelectionEvent>();
  @Output() startTargetSelection: EventEmitter<StartTargetSelectionEvent> = new EventEmitter<
    StartTargetSelectionEvent
  >();
  @Output() cancelTargetSelection: EventEmitter<CancelTargetSelectionEvent> = new EventEmitter<
    CancelTargetSelectionEvent
  >();
  @Output() initialized = new EventEmitter<void>();
  cursorNode: any;
  startedGhostEdge = false;

  public removedNodeId = new EventEmitter<string>();

  cy: any;

  @HostListener('mouseup', ['$event'])
  @HostListener('mousedown', ['$event'])
  onMouseUp(event: MouseEvent) {
    // @ts-ignore
    const rect = event.target.getBoundingClientRect(),
      offsetX = event.clientX - rect.left,
      offsetY = event.clientY - rect.top;

    const position = {
      x: (offsetX - this.pan.x) / this.zoom,
      y: (offsetY - this.pan.y) / this.zoom,
    };

    if (event.type === 'mouseup') {
      this.flowDesignerService.mousedown = false;
      this.mouseUp.emit({
        type: 'mouseUp',
        position,
        originalEvent: event,
      });
    } else {
      this.flowDesignerService.mousedown = true;
      this.mouseUp.emit({
        type: 'mouseDown',
        position,
        originalEvent: event,
      });
    }
  }

  @HostListener('window:keydown.arrowup', ['$event'])
  @HostListener('window:keydown.arrowleft', ['$event'])
  @HostListener('window:keydown.arrowdown', ['$event'])
  @HostListener('window:keydown.arrowright', ['$event'])
  onArrowKey(event) {
    if (document.activeElement.tagName !== 'BODY') {
      return;
    }
    if (!this.moveOnArrows) {
      return;
    }
    const pan = { ...this.pan };
    switch (event.key) {
      case 'ArrowUp':
        pan.y += this.arrowsMoveSensitive;
        break;
      case 'ArrowDown':
        pan.y -= this.arrowsMoveSensitive;
        break;
      case 'ArrowLeft':
        pan.x += this.arrowsMoveSensitive;
        break;
      case 'ArrowRight':
        pan.x -= this.arrowsMoveSensitive;
        break;
    }
    this.cy.pan(pan);
    event.preventDefault();
  }

  forceRender() {
    this.cy._private.renderer.render();
  }

  removeNode(id: string) {
    const element = this.cy.getElementById(id);

    if (element.data('isNode')) {
      element.connectedEdges().forEach(edge => {
        edge.connectedNodes().forEach(output => {
          if (output.data('isOutput')) {
            output.data('directive').targetChange.emit(undefined);
          }
        });
      });
    }

    this.cy.remove(element);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.cy) {
      if (changes.pan && !changes.pan.firstChange) {
        this.cy.animate({ pan: this.pan, zoom: 1 }, { duration: this.panAnimationDuration, easing: 'ease-in-out' });
      } else if (changes.zoom && !changes.zoom.firstChange) {
        this.cy.animate({ zoom: this.zoom }, { duration: this.panAnimationDuration / 2, easing: 'ease-in-out' });
      }
    }
  }

  ngOnInit() {
    this.ngZone.runOutsideAngular(() => {
      // @ts-ignore
      this.cy = cytoscape({
        container: this.container.nativeElement,
        debug: this.debug,
        wheelSensitivity: this.wheelSensitivity,
        maxZoom: Number(this.maxZoom),
        minZoom: Number(this.minZoom),
        zoom: this.zoom,
        pan: this.pan,
        boxSelectionEnabled: false,
        renderer: {
          name: 'flow-designer-svg',
        },
        motionBlur: {
          smallGridCellSize: this.smallGridCellSize,
          gridCellSize: this.gridCellSize,
        },
        layout: {
          name: 'flow-designer',
        },
        style: styles,
      } as CytoscapeOptions);

      this.cy.flowDesigner({
        smallGridCellSize: this.smallGridCellSize,
        gridCellSize: this.gridCellSize,
      });

      this.zoom = this.cy.zoom();
      this.pan = this.cy.pan();

      this.cy.on('zoom', () => {
        this.zoom = this.cy.zoom();
        this.zoomChange.emit(this.zoom);
        setTimeout(() => this.forceRender());
      });
      this.cy.on('pan', () => {
        this.pan = this.cy.pan();
        this.panChange.emit(this.pan);
      });

      this.cy.on('grabon', event => {
        const nodeDirective = this.nodes.find(n => n.id === event.target.id());
        if (nodeDirective) {
          nodeDirective.dragStart.emit(event);
        }
      });

      this.cy.on('dragfree', event => {
        const nodeDirective = this.nodes.find(n => n.id === event.target.id());

        if (nodeDirective) {
          event.position = event.target.position();
          nodeDirective.dragStop.emit(event);
        }
      });

      this.cy.on('free', ({ target }) => {
        if (!this.snapToGrid) {
          return;
        }
        let { x, y } = target.position();
        x += target.width() / 2;
        y -= target.height() / 2;

        x = this.smallGridCellSize * Math.round(x / this.smallGridCellSize);
        y = this.smallGridCellSize * Math.round(y / this.smallGridCellSize);

        x -= target.width() / 2;
        y += target.height() / 2;

        target.position({ x, y });
        this.forceRender();
      });

      this.cy.on('mousedown', e => {
        ``;
        const node = e.target;
        if (node !== this.cy && node.data('isOutput') && !node.data('directive').parentNode.isLocked()) {
          this.startTargetSelection.emit({
            source: e.target.id(),
            originalEvent: e.originalEvent,
          });
          this.startGhostEdge(e.target.id());
        }
      });

      this.cy.on('mouseup', e => {
        if (this.startedGhostEdge) {
          this.stopGhostEdge(e.target, e);
        }
      });

      this.cy.on('add', () => {
        this.forceRender();
      });

      this.cy.panningEnabled(this.panEnabled);
      this.cy.zoomingEnabled(this.zoomEnabled);

      this.cy.on('mousemove', ({ position }) => {
        position.x -= 5 / this.zoom;
        if (this.cursorNode) {
          this.cursorNode.position(position);
        }
      });
    });

    this.ngFlowDesignerService.setCurrentCy(this.cy);
  }

  ngAfterContentInit(): void {
    this.initialized.next();
  }

  ngOnDestroy(): void {
    this.ngFlowDesignerService.setCurrentCy(undefined);
  }

  private startGhostEdge(outputId: string) {
    this.createCursorNode(this.cy.getElementById(outputId).position());
    this.startedGhostEdge = true;
    this.cy.add({
      data: {
        id: 'ghost-edge',
        source: outputId,
        target: this.cursorNode.id(),
      },
    });
  }

  private stopGhostEdge(target: any, e: any) {
    const ghostEdge = this.cy.getElementById('ghost-edge');
    const source = ghostEdge.source();
    const outputDirective: NgFlowDesignerOutputDirective = source.data('directive');

    if (target !== this.cy && target.data('isNode')) {
      outputDirective.setTarget(target.id());
      this.targetSelection.emit({
        source: outputDirective.id,
        target: target.id(),
        originalEvent: e.originalEvent,
      });
    } else {
      if (outputDirective.target) {
        this.targetSelection.emit({
          source: outputDirective.id,
          target: undefined,
          originalEvent: e.originalEvent,
        });
      }
      outputDirective.setTarget(undefined);
      this.cancelTargetSelection.emit({
        source: outputDirective.id,
        position: this.cursorNode.position(),
        originalEvent: e.originalEvent,
      });
    }

    this.cy.remove(ghostEdge);
    this.startedGhostEdge = false;
    this.removeCursorNode();
  }

  private createCursorNode(position: Position) {
    this.cy.add({
      data: {
        id: 'cursor-node',
      },
    });

    this.cursorNode = this.cy.getElementById('cursor-node');
    this.cursorNode.position(position);
    this.cursorNode.style({
      width: 1,
      height: 1,
    });
  }

  private removeCursorNode() {
    this.cy.remove(this.cursorNode);
    this.cursorNode = undefined;
  }
}
